import {
  IPdfDefinitions,
  IPdfData,
  PdfContentType,
  IPdfValueParams,
  PdfFiledValue,
  SEARCH_SUMMARY_DEFINE,
  PdfContentSetting,
  PdfSelectColumn,
  CHARGEABLE_TIPS_DEFINE,
  CHARGEABLE_COLUMN_DEFINE,
  CHARGEABLE_SELECT_COLUMN_DEFINE,
  IPdfSignature,
  SignatureConfiguration,
  TotalsConfiguration,
} from '@app/shared/common/make-pdf/v2';
import {
  IInventoryItem,
  ITransferFormFieldPath,
  MEASURE_TRANSACTION,
} from '@app/shared/types';
import {
  generateSpacesHeader,
  generateSpacesText,
  groupByMajorMinorCategoryAndSort,
  getSingleExportFileName,
  formatDateWithTimeZone,
  isCategoryView,
  generateUnitTextV2,
} from '@app/shared/utils';
import { ContentText, Style } from 'pdfmake/interfaces';
import {
  includes,
  get,
  isEqual,
  map,
  isNil,
  isEmpty,
  find,
  every,
  isArray,
  isPlainObject,
} from 'lodash';
import { TIME_FORMATS } from '@config/global-constants';
import { TRANSFER_FORM_FIELD_PATHS } from './transfer-form-field-paths';
import {
  calculateResultOfDivFactorValue,
  transferTypeFormatter,
  transferPDFQuantityFormatter,
  transferStatusFormatter,
} from './transfer.util';
import { DecimalNumber } from '@app/shared/common';
import { PdfWorkerSelfType } from '@app/shared/common/make-pdf/pdf-worker.type';
import { TRANSFER_FINAL_STATUS } from './transfer.definition';
import { dataSourceFormatter } from '@app/shared/formatters';
import { IPdfSpecialOptions } from './transfer-export.type';
import { I18n } from '@app/i18n/strings-keys';
import { TRANSFER_DEFAULT_SIGNATURE_LINES_COLUMN } from './transfer-pdf-signature-config';
import { QTY_PULLED_COLUMN_DEFINE } from './transfer-constants';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';

type TransferPdfConfigBuilder = (
  data: IPdfDefinitions['data'],
  modalColumns: PdfSelectColumn[],
  bulkOptions?: IPdfDefinitions['bulkOptions'],
  specialOptions?: IPdfSpecialOptions,
) => IPdfDefinitions;

interface TransferPdfBuildOptions {
  isFromDetail: boolean;
  signatureConfiguration?: SignatureConfiguration;
  totalsConfiguration?: TotalsConfiguration;
}

declare const self: PdfWorkerSelfType;

/**
 * Get default value for totals toggle based on transfer count and status
 * - Single transfer print: default is OFF
 * - Multiple transfers with all Open or Pending status: default is OFF
 * - All other cases: default is ON
 *
 * @param data - Single transfer data
 * @param bulkData - Array of transfers for bulk operations
 * @returns false for single transfer or all Open/Pending, true otherwise
 */
const getTotalsDefaultValue = (data: IPdfData[] | IPdfData): boolean => {
  let totalFieldValue = false;
  if (isArray(data)) {
    const allOpenOrPending = every(data, (item) => {
      const itemStatus = get(item, 'status');

      return includes(
        [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
        itemStatus,
      );
    });

    // All Open or Pending: OFF, otherwise: ON
    totalFieldValue = !allOpenOrPending;
  } else if (isPlainObject(data)) {
    const dataStatus = get(data, 'status');
    totalFieldValue = !includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      dataStatus,
    );
  }

  return totalFieldValue;
};

const getTransferPdfConfigBuilder = (
  formFieldPath: ITransferFormFieldPath,
  buildOptions: TransferPdfBuildOptions,
): TransferPdfConfigBuilder => {
  const {
    isFromDetail,
    signatureConfiguration = false,
    totalsConfiguration,
  } = buildOptions;

  return (
    data: IPdfDefinitions['data'],
    modalColumns: PdfSelectColumn[],
    bulkOptions?: IPdfDefinitions['bulkOptions'],
    specialOptions?: IPdfSpecialOptions,
  ): IPdfDefinitions => {
    const {
      isTransferGroup = false,
      i18nService,
      totalsConfiguration: dynamicTotalsConfiguration,
    } = specialOptions || {};

    let effectiveTotalsConfiguration =
      dynamicTotalsConfiguration ?? totalsConfiguration;
    if (!effectiveTotalsConfiguration) {
      effectiveTotalsConfiguration = {
        enabled: true,
      };
    }

    return {
      bulkOptions,
      data,
      generateFileName: (pdfData): string => {
        const pdfEntityString = i18nService.instant(
          I18n.Forms.TransferForm.TransferSummary,
        );
        const siteNameTo = get(pdfData, 'site_name_to');
        const transactionDate = get(pdfData, 'transaction_date') as string;

        return getSingleExportFileName(
          pdfEntityString,
          siteNameTo,
          transactionDate,
        );
      },
      generateHeader: (pdfData: IPdfData): ContentText[] => {
        const headerTitle = i18nService.instant(
          I18n.Forms.TransferForm.TransferSummary,
        );

        return [
          {
            text: generateSpacesHeader(
              headerTitle,
              pdfData?.internal_number?.toString(),
            ),
            fontSize: 14,
            bold: true,
            margin: [20, 20, 20, 0],
          },
        ];
      },
      content: {
        summary: {
          type: PdfContentType.Summary,
          setting: (_, __, tools) => ({
            fields: [
              {
                [I18n.Forms.TransferForm.TransferType]: {
                  path: 'transfer_type',
                  width: '33%',
                  valueGetter: (params: IPdfValueParams): string =>
                    transferTypeFormatter(
                      {
                        node: null,
                        data: null,
                        colDef: null,
                        column: null,
                        api: null,
                        columnApi: null,
                        context: null,
                        value: params.value,
                      },
                      { i18nService: tools.services.i18nService },
                    ),
                },
                [I18n.Common.From]: {
                  path: TRANSFER_FORM_FIELD_PATHS.siteId,
                  width: '33%',
                  valueGetter: (params: IPdfValueParams): string =>
                    generateSpacesText(
                      params.data?.site_name?.toString(),
                      params?.data.store_number?.toString(),
                    ),
                },
                [I18n.Common.Status]: {
                  path: 'status',
                  valueFormatter: (params) =>
                    transferStatusFormatter(params, {
                      i18nService: tools.services.i18nService,
                    }),
                  width: '34%',
                },
              },
              {
                [I18n.Forms.TransferForm.TransferDate]: {
                  path: 'transaction_date',
                  valueGetter: (params): string =>
                    formatDateWithTimeZone(
                      params.value?.toString(),
                      params.tools.services.companyService.currentCompany
                        ?.timezone,
                      TIME_FORMATS.L,
                    ),
                  width: '33%',
                },
                [I18n.Common.To]: {
                  path: TRANSFER_FORM_FIELD_PATHS.siteIdTo,
                  valueGetter: (params: IPdfValueParams): string =>
                    generateSpacesText(
                      params.data?.site_name_to?.toString(),
                      params?.data.store_number_to?.toString(),
                    ),
                  width: '33%',
                },
                [I18n.EntityNames.Event]: {
                  path: 'event_id',
                  width: '34%',
                  hidden: (params): boolean => {
                    return isEmpty(params.value);
                  },
                },
              },
              {
                [I18n.Common.Source]: {
                  path: TRANSFER_FORM_FIELD_PATHS.source,
                  width: '33%',
                  valueFormatter: (params) =>
                    dataSourceFormatter(params as any, {
                      i18nService: tools.services.i18nService,
                    }),
                },
                [I18n.Forms.TransferForm.TransferGroupName]: {
                  path: TRANSFER_FORM_FIELD_PATHS.transferGroupName,
                  width: '67%',
                  hidden: (): boolean => {
                    return !isTransferGroup;
                  },
                },
              },
              SEARCH_SUMMARY_DEFINE,
            ],
          }),
        },
        tips: CHARGEABLE_TIPS_DEFINE,
        mainGrids: {
          type: PdfContentType.Grids,
          setting: (pdfData: IPdfData): PdfContentSetting => {
            const isNotFinalTransfer = pdfData.isNotFinalTransfer;
            const isVisibilityRequestCost = pdfData.isVisibilityRequestCost;
            const displayQoh = pdfData?.displayQoh;
            const hiddenQohFromColumn = (): boolean => !displayQoh;
            const hiddenParQtyColumn = (): boolean =>
              includes(TRANSFER_FINAL_STATUS, pdfData.status);

            return {
              [formFieldPath.virtualInventoryItemVenues]: {
                title: ({ gridData, tools }): string => {
                  return isCategoryView(
                    tools.services?.stateService?.getTransferItemListViewSetting(),
                  )
                    ? tools.services.i18nService.instant(
                        I18n.Forms.GridForm.ExportMajorMinorTitle,
                        {
                          majorCategory:
                            gridData[0]?.major_category_name ??
                            gridData[0]?.majorCategory ??
                            '',
                          minorCategory:
                            gridData[0]?.minor_category_name ??
                            gridData[0]?.minorCategory ??
                            '',
                        },
                      )
                    : null;
                },
                orderBy: (): string[] | null =>
                  !isFromDetail
                    ? ['is_chargeable desc', 'name asc', 'item_name asc']
                    : null,
                columns: [
                  CHARGEABLE_COLUMN_DEFINE,
                  {
                    path: 'name',
                    title: I18n.Forms.GridForm.ItemName,
                    valueGetter: (params): string =>
                      params.value ?? params.rowData?.item_name,
                  },
                  {
                    path: 'gl_account_name',
                    title: I18n.Forms.GridForm.GLAccount,
                  },
                  {
                    hiddenColumn: hiddenQohFromColumn,
                    path: 'qoh_from',
                    title: I18n.Forms.TransferForm.QOHFrom,
                    alignment: 'right',
                    valueGetter: (params): string => {
                      const complexData = {
                        value: params.value,
                        tools: params.tools,
                        pendingQty: params?.rowData?.pending_qty_from,
                        measureFactorValue:
                          params?.rowData?.measure_factor_value,
                      };

                      return calculateResultOfDivFactorValue(complexData, true);
                    },
                  },
                  {
                    path: 'qoh_to',
                    title: I18n.Forms.TransferForm.QOHTo,
                    alignment: 'right',
                    valueGetter: (params): string => {
                      const complexData = {
                        value: params.value,
                        tools: params.tools,
                        pendingQty: params?.rowData?.pending_qty_to,
                        measureFactorValue:
                          params?.rowData?.measure_factor_value,
                      };

                      return calculateResultOfDivFactorValue(complexData, true);
                    },
                  },
                  {
                    path: 'transfer_unit',
                    title: I18n.Forms.TransferForm.TransferUnit,
                    valueGetter: ({ tools, ...params }): string => {
                      return generateUnitTextV2(
                        {
                          description: get(
                            params.rowData,
                            'measure_description',
                            '',
                          ),
                          factor_value: get(
                            params.rowData,
                            'measure_factor_value',
                            '',
                          ),
                        },
                        {
                          numberFormatService:
                            tools.services.numberFormatService,
                          measureTransactionType: MEASURE_TRANSACTION.Transfer,
                          notNeedAddSign: true,
                        },
                      );
                    },
                  },
                  {
                    path: 'par_qty',
                    title: I18n.Forms.TransferForm.PARQty,
                    alignment: 'right',
                    hiddenColumn: hiddenParQtyColumn,
                    valueFormatter: ({ tools, ...params }): string =>
                      tools.services.numberFormatService.getNumberFormatter(
                        false,
                      )(params),
                  },
                  {
                    path: 'initial_count',
                    title: I18n.Forms.TransferForm.QtyRequested,
                    alignment: 'right',
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                  QTY_PULLED_COLUMN_DEFINE,
                  {
                    path: 'item_cost',
                    title: I18n.Forms.GridForm.UnitCost,
                    alignment: 'right',
                    valueFormatter: ({ tools, ...params }): string =>
                      tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
                  },
                  {
                    path: 'total_cost',
                    title: I18n.Forms.TransferForm.RequestCost,
                    alignment: 'right',
                    hiddenColumn: !isNotFinalTransfer,
                    valueFormatter: ({ tools, ...params }): string =>
                      tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
                    valueGetter: (params): string | DecimalNumber => {
                      const unitCost = params.rowData.item_cost;
                      const calcCount = params.rowData.initial_count;

                      return !isNil(params.rowData.initial_count)
                        ? new DecimalNumber(calcCount).mul(unitCost)
                        : '';
                    },
                  },
                  {
                    path: 'count',
                    title: I18n.Forms.TransferForm.QtyReceived,
                    alignment: 'right',
                    hiddenColumn: isNotFinalTransfer,
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                  {
                    path: 'total_cost',
                    title: I18n.Forms.TransferForm.ReceivedCost,
                    alignment: 'right',
                    hiddenColumn: isNotFinalTransfer,
                    valueFormatter: ({ tools, ...params }): string =>
                      tools.services.numberFormatService.getNumberFormatter(
                        true,
                      )(params),
                    valueGetter: (params): string | DecimalNumber => {
                      const unitCost = params.rowData.item_cost;
                      const calcCount = params.rowData.count;

                      return !isNil(params.rowData.count)
                        ? new DecimalNumber(calcCount).mul(unitCost)
                        : '';
                    },
                  },
                ],
                multiGrid: (
                  params,
                ):
                  | Record<string, IInventoryItem[]>
                  | PdfFiledValue<IInventoryItem>[] =>
                  isCategoryView(
                    params.tools.services?.stateService?.getTransferItemListViewSetting(),
                  )
                    ? groupByMajorMinorCategoryAndSort(params?.data)
                    : [params.data],
              },
              [formFieldPath.virtualTotals]: {
                hidden: (params): boolean => {
                  // Hide totals table when totals option is false
                  return !get(params, 'options.totals', true);
                },
                title: {
                  text: I18n.Common.Totals,
                  style: {
                    fontSize: 11,
                    bold: true,
                    margin: [0, 10, 0, 5],
                  },
                },
                rowStyle: (params): Style => {
                  return {
                    bold: params.gridData.length - 1 === params.rowIndex,
                  };
                },
                layout: {
                  fillColor: (rowIndex): string => {
                    return rowIndex % 2 === 0
                      ? self.greyBgColor
                      : self.whiteBgColor;
                  },
                },
                columnDefaultStyle: {
                  width: '*',
                  alignment: 'right',
                },
                columns: [
                  {
                    path: 'site_department_code_to',
                    title: I18n.EntityNames.DepartmentCode,
                    width: '*',
                    alignment: null,
                    hiddenColumn: (params): boolean => {
                      return !find(params.options.selectedColumns, (column) =>
                        isEqual(column.title, I18n.EntityNames.DepartmentCode),
                      );
                    },
                  },
                  {
                    path: 'category',
                    title: '',
                    width: '*',
                    alignment: 'left',
                  },
                  {
                    path: 'name',
                    title: I18n.Forms.GridForm.GLAccount,
                    width: '*',
                    alignment: 'left',
                    hiddenColumn: (params): boolean => {
                      const status = get(params, 'data.status');
                      const isOpenOrPendingTransfer = includes(
                        [
                          TRANSFER_STATUS_VALUE.Open,
                          TRANSFER_STATUS_VALUE.Pending,
                        ],
                        status,
                      );

                      return isOpenOrPendingTransfer;
                    },
                  },
                  {
                    path: 'initial_count',
                    title: I18n.Forms.TransferForm.QtyRequested,
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                  {
                    path: 'total_cost',
                    title: I18n.Forms.TransferForm.RequestCost,
                    hiddenColumn: (params): boolean => {
                      const selectedColumns = map(
                        params.options.selectedColumns,
                        (column) => column.title,
                      );
                      if (
                        isNotFinalTransfer &&
                        !includes(
                          selectedColumns,
                          I18n.Forms.TransferForm.RequestCost,
                        )
                      ) {
                        return true;
                      }

                      return !isNotFinalTransfer && !isVisibilityRequestCost;
                    },
                    valueFormatter: ({ tools, ...params }): string =>
                      !isNil(params.value)
                        ? tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params)
                        : null,
                  },
                  {
                    path: 'quantity_reversed',
                    title: I18n.Forms.TransferForm.QtyReversed,
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                  {
                    path: 'reversed_cost',
                    title: I18n.Forms.TransferForm.ReversedCost,
                    hiddenColumn: (params): boolean => {
                      const selectedColumns = map(
                        params.options.selectedColumns,
                        (column) => column.title,
                      );
                      if (
                        isNotFinalTransfer &&
                        !includes(
                          selectedColumns,
                          I18n.Forms.TransferForm.RequestCost,
                        )
                      ) {
                        return true;
                      }

                      return !isNotFinalTransfer && !isVisibilityRequestCost;
                    },
                    valueFormatter: ({ tools, ...params }): string =>
                      !isNil(params.value)
                        ? tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params)
                        : null,
                  },
                  {
                    path: 'count',
                    title: I18n.Forms.TransferForm.QtyReceived,
                    hiddenColumn: isNotFinalTransfer,
                    valueFormatter: transferPDFQuantityFormatter,
                  },
                  {
                    path: 'received_total_cost',
                    title: I18n.Forms.TransferForm.ReceivedCost,
                    hiddenColumn: (params): boolean => {
                      const selectedColumns = map(
                        params.options.selectedColumns,
                        (column) => column.title,
                      );
                      if (
                        !isNotFinalTransfer &&
                        !includes(
                          selectedColumns,
                          I18n.Forms.TransferForm.ReceivedCost,
                        )
                      ) {
                        return true;
                      }

                      return isNotFinalTransfer;
                    },
                    valueFormatter: ({ tools, ...params }): string =>
                      !isNil(params.value)
                        ? tools.services.numberFormatService.getNumberFormatter(
                            true,
                          )(params)
                        : null,
                  },
                ],
              },
            };
          },
        },
        signatures: {
          type: PdfContentType.Signatures,
          setting: (): IPdfSignature[][] =>
            TRANSFER_DEFAULT_SIGNATURE_LINES_COLUMN,
        },
      },
      options: {
        pageMargins: [20, 50, 20, 60],
        columnSelector: {
          columns: [CHARGEABLE_SELECT_COLUMN_DEFINE, ...modalColumns],
          effectCondition: {
            type: 'excludes',
            paths: [formFieldPath.virtualTotals],
          },
          signatureConfiguration,
          totalsConfiguration: effectiveTotalsConfiguration,
        },
      },
    };
  };
};

export {
  TransferPdfConfigBuilder,
  getTransferPdfConfigBuilder,
  getTotalsDefaultValue,
};
